import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { X, ChevronRight, ChevronLeft, Sparkles, Camera, BookOpen, Briefcase, DollarSign, Mic, Mail, Linkedin } from 'lucide-react';
import { trackExecOnboardingComplete, trackBookDemoClick } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_INDUSTRIES = ['SaaS', 'Fintech', 'Healthtech', 'Automotive', 'Manufacturing', 'Retail', 'Marketing', 'Sales', 'HR', 'Operations', 'Real Estate', 'Logistics', 'Education'];
const OFFERING_RATE_FIELDS = [
  { key: 'endorsement_post', label: 'Endorsement Post', range: '$200–$2,500' },
  { key: 'written_testimonial', label: 'Written Testimonial', range: '$500–$1,500' },
  { key: 'video_testimonial', label: 'Video Testimonial', range: '$750–$2,500' },
  { key: 'advisory_call', label: 'Advisory Call (per hr)', range: '$250–$600' },
  { key: 'consulting_project', label: 'Consulting Day Rate', range: '$1,500–$5,000' },
];

const STEPS = [
  { id: 'photo', title: 'Photo & Headline', icon: Camera },
  { id: 'bio', title: 'About You', icon: BookOpen },
  { id: 'industries', title: 'Industries', icon: Briefcase },
  { id: 'socials', title: 'Social Links', icon: Linkedin },
  { id: 'rates', title: 'Your Rates', icon: DollarSign },
  { id: 'pitch', title: 'Sales Pitch', icon: Mic },
  { id: 'newsletter', title: 'Newsletter', icon: Mail },
];

export default function OnboardingWizard({ user, onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    headshot_url: user?.headshot_url || '',
    headline: user?.headline || '',
    bio: user?.bio || '',
    industries: user?.industries || [],
    linkedin_url: user?.linkedin_url || '',
    x_handle: user?.x_handle || '',
    standard_rates: user?.standard_rates || {},
    open_to_sales_pitch: user?.open_to_sales_pitch ?? false,
    sales_pitch_rate: user?.sales_pitch_rate || '',
    sales_pitch_min_minutes: user?.sales_pitch_min_minutes || 30,
    has_newsletter: user?.has_newsletter ?? false,
    newsletter_url: user?.newsletter_url || '',
    newsletter_subscribers: user?.newsletter_subscribers || '',
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = (k, v) => setData(d => ({ ...d, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      setUploading(true);
      const { data: r } = await axios.post(`${API}/upload`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      setField('headshot_url', r.serve_url);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const finish = async (skipped = false) => {
    setBusy(true);
    try {
      const payload = { ...data };
      ['sales_pitch_rate', 'sales_pitch_min_minutes', 'newsletter_subscribers'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) { delete payload[k]; }
        else { payload[k] = Number(payload[k]); }
      });
      // Clean up rates
      const cleanRates = {};
      Object.entries(payload.standard_rates || {}).forEach(([k, v]) => { if (v) cleanRates[k] = Number(v); });
      payload.standard_rates = cleanRates;
      await axios.post(`${API}/users/me/complete-onboarding`, payload, { headers: getAuthHeaders() });
      // Only count as a conversion when the exec actually finished (not skipped).
      if (!skipped) {
        trackExecOnboardingComplete({
          industries_count: (payload.industries || []).length,
          has_newsletter: !!payload.has_newsletter,
          open_to_sales_pitch: !!payload.open_to_sales_pitch,
        });
      }
      toast.success(skipped ? "Saved — you can finish anytime from Profile." : "All set — welcome aboard!");
      onComplete && onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  const Cur = STEPS[step];
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative glass max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-white/10 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF4343]" />
              <h2 className="text-sm font-semibold text-white">Welcome to <span className="text-[#FF4343]">Freddy Leads</span></h2>
            </div>
            <button onClick={onSkip} data-testid="onboarding-close" className="text-[#9CA3AF] hover:text-white text-xs">Maybe later</button>
          </div>
          <p className="text-[10px] text-[#6B7280] mt-1">Step {step + 1} of {STEPS.length} · {Cur.title}</p>
          <div className="h-1 bg-white/5 mt-3">
            <div className="h-full bg-[#FF4343] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="step-photo">
              <p className="text-xs text-[#9CA3AF]">A clear headshot + sharp headline doubles your invite rate.</p>
              <div className="flex items-center gap-4">
                {data.headshot_url ? (
                  <img src={data.headshot_url} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#FF4343]/40" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><Camera className="w-6 h-6 text-[#6B7280]" /></div>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="onboarding-photo-input" />
                  <span className="text-xs px-3 py-2 border border-white/20 text-[#FF4343] hover:bg-[#FF4343]/10 inline-block">{uploading ? 'Uploading…' : (data.headshot_url ? 'Replace photo' : 'Upload photo')}</span>
                </label>
              </div>
              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Headline *</label>
                <input value={data.headline} onChange={e => setField('headline', e.target.value)} data-testid="onboarding-headline"
                  placeholder="e.g., VP Sales at Acme · ex-Salesforce · GTM operator"
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="step-bio">
              <p className="text-xs text-[#9CA3AF]">A short bio (under 200 chars) — what brands see in the directory.</p>
              <textarea value={data.bio} onChange={e => setField('bio', e.target.value.slice(0, 220))} rows={4} data-testid="onboarding-bio"
                placeholder="Built and scaled 3 SaaS GTM teams from $5M to $50M ARR. Today I write about pipeline operations and lead my own consulting practice."
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] resize-none" />
              <p className="text-[10px] text-[#6B7280] text-right">{data.bio.length}/220</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="step-industries">
              <p className="text-xs text-[#9CA3AF]">Pick 1-3 industries you have deep expertise in. Brands filter on this.</p>
              <div className="flex flex-wrap gap-2">
                {ALL_INDUSTRIES.map(ind => {
                  const on = (data.industries || []).includes(ind);
                  return (
                    <button key={ind} onClick={() => {
                      const cur = data.industries || [];
                      if (on) setField('industries', cur.filter(i => i !== ind));
                      else if (cur.length < 3) setField('industries', [...cur, ind]);
                    }} data-testid={`onboarding-industry-${ind}`}
                      className={`px-3 py-1.5 text-xs border ${on ? 'bg-[#FF4343] text-[#090E33] border-[#FF4343] font-semibold' : 'border-white/20 text-[#9CA3AF] hover:border-white/40'}`}>{ind}</button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="step-socials">
              <p className="text-xs text-[#9CA3AF]">Connect your real audience. We never post on your behalf.</p>
              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block flex items-center gap-1"><Linkedin className="w-3 h-3" /> LinkedIn URL</label>
                <input value={data.linkedin_url} onChange={e => setField('linkedin_url', e.target.value)} data-testid="onboarding-linkedin"
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">X / Twitter handle</label>
                <input value={data.x_handle} onChange={e => setField('x_handle', e.target.value.replace('@', ''))} data-testid="onboarding-x"
                  placeholder="yourhandle"
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="step-rates">
              <p className="text-xs text-[#9CA3AF]">Set your standard rate per offering. You can always override per-campaign.</p>
              {OFFERING_RATE_FIELDS.map(o => (
                <div key={o.key} className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-white">{o.label}</p>
                    <p className="text-[10px] text-[#6B7280]">Market range: {o.range}</p>
                  </div>
                  <div className="flex items-center bg-[#090E33] border border-white/10 px-2 w-32">
                    <span className="text-xs text-[#6B7280]">$</span>
                    <input type="number" value={data.standard_rates[o.key] || ''} onChange={e => setField('standard_rates', { ...data.standard_rates, [o.key]: e.target.value })}
                      placeholder="0" data-testid={`onboarding-rate-${o.key}`}
                      className="flex-1 px-1 py-1.5 bg-transparent text-white text-sm focus:outline-none" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3" data-testid="step-pitch">
              <p className="text-xs text-[#9CA3AF]">Brands sometimes want 15-45 min of your time to pitch you their product. You can opt-in and set your own rate.</p>
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 p-3">
                <input type="checkbox" checked={data.open_to_sales_pitch} onChange={e => setField('open_to_sales_pitch', e.target.checked)} data-testid="onboarding-pitch-toggle"
                  className="w-4 h-4 accent-[#FF4343]" />
                <label className="text-sm text-white flex-1">I'm open to paid sales-pitch meetings</label>
              </div>
              {data.open_to_sales_pitch && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Your rate ($ per meeting) *</label>
                    <input type="number" value={data.sales_pitch_rate} onChange={e => setField('sales_pitch_rate', e.target.value)} data-testid="onboarding-pitch-rate"
                      placeholder="500"
                      className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Minimum meeting length (min)</label>
                    <select value={data.sales_pitch_min_minutes} onChange={e => setField('sales_pitch_min_minutes', e.target.value)} data-testid="onboarding-pitch-min"
                      className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                      {[15, 20, 30, 45, 60].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                  </div>
                  <p className="text-[10px] text-[#6B7280]">24-hour cancellation policy applies. You're paid 100% on confirmation.</p>
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3" data-testid="step-newsletter">
              <p className="text-xs text-[#9CA3AF]">If you run a newsletter, brands may book endorsements there too.</p>
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 p-3">
                <input type="checkbox" checked={data.has_newsletter} onChange={e => setField('has_newsletter', e.target.checked)} data-testid="onboarding-newsletter-toggle"
                  className="w-4 h-4 accent-[#FF4343]" />
                <label className="text-sm text-white flex-1">Yes, I have a newsletter</label>
              </div>
              {data.has_newsletter && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Newsletter URL</label>
                    <input value={data.newsletter_url} onChange={e => setField('newsletter_url', e.target.value)} data-testid="onboarding-newsletter-url"
                      placeholder="https://yournewsletter.substack.com"
                      className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Subscribers</label>
                    <input type="number" value={data.newsletter_subscribers} onChange={e => setField('newsletter_subscribers', e.target.value)} data-testid="onboarding-newsletter-subs"
                      placeholder="1500"
                      className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                </div>
              )}

              {/* Meet with the Freddy team CTA */}
              <div className="mt-6 bg-gradient-to-br from-[#FF4343]/10 to-[#111827] border border-[#FF4343]/40 p-4">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] mb-1">Want a Personal Walkthrough?</p>
                <p className="text-sm text-white font-semibold mb-1">Book 15 minutes with Jon &amp; Noah</p>
                <p className="text-xs text-[#9CA3AF] mb-3 leading-relaxed">The founders of Freddy Leads. We'll show you how to maximize your earnings, find the right campaigns for your audience, and answer any questions.</p>
                <a href="https://meetings.hubspot.com/jon980" target="_blank" rel="noopener noreferrer"
                  data-testid="onboarding-book-call"
                  onClick={() => trackBookDemoClick({ source: 'exec_onboarding_wizard' })}
                  className="inline-block border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-xs font-semibold hover:bg-[#E03030]">
                  Book My Walkthrough →
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-4 flex items-center justify-between">
          <button onClick={back} disabled={step === 0 || busy} className="text-xs text-[#9CA3AF] hover:text-white disabled:opacity-30 flex items-center gap-1" data-testid="onboarding-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={busy} data-testid="onboarding-next"
              className="bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={() => finish(false)} disabled={busy} data-testid="onboarding-finish"
              className="bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">
              {busy ? 'Saving…' : 'Finish'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
